import { Component, OnInit, inject, ViewChild, ElementRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormTemplateService } from 'src/app/Services/Forms/form-template.service';
import { UserDataService } from 'src/app/Services/Authentication/User/user-data.service';
import { Form_Template_Field, Form_Template_With_Fields, Response_Forms } from 'src/app/Interfaces/Forms/FormTemplate.interface';
import { GeneralRoleCDMS, typeSystemRoleCDMS } from 'src/app/types/General/RoleCDMS.type';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import Swal from 'sweetalert2';

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
}

@Component({
  selector: 'app-form-genai-cdms',
  templateUrl: './form-genai-cdms.component.html',
  styleUrls: ['./form-genai-cdms.component.scss', '../../../Styles/GlobalStyles.css'],
  standalone: false,
  animations: [
    trigger('slideEditor', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)', maxHeight: '0' }),
        animate('280ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 1, transform: 'translateY(0)', maxHeight: '55%' }))
      ]),
      transition(':leave', [
        animate('200ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 0, transform: 'translateY(20px)', maxHeight: '0' }))
      ])
    ])
  ]
})
export class FormGenAICDMSComponent implements OnInit {

  @ViewChild('chatMessages') chatMessagesEl!: ElementRef;
  @ViewChild('fileInput') fileInputEl!: ElementRef;

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private formTemplateService = inject(FormTemplateService);
  public UserService = inject(UserDataService);
  public RolesGeneral: typeSystemRoleCDMS = GeneralRoleCDMS;

  // Form schema state
  public FormSchema: {
    Name: string;
    Description: string | null;
    Category: string | null;
    Fields: Form_Template_Field[];
  } = { Name: '', Description: null, Category: null, Fields: [] };

  // Chat state
  public ChatHistory: ChatMessage[] = [];
  public ApiChatHistory: any[] = []; // Raw history for API
  public UserInput: string = '';
  public IsLoading: boolean = false;
  public SelectedFile: File | null = null;

  // Template state
  public TemplateId: number | null = null;
  public IsEdit: boolean = false;

  // Toolbar panels
  // Toolbar panels
  public ShowSettings: boolean = false;

  public SelectedField: Form_Template_Field | null = null;
  public ShowFieldEditor: boolean = false;

  // Field editor form
  public FieldForm: FormGroup = new FormGroup({
    Label: new FormControl('', Validators.required),
    Field_Type: new FormControl('text_short', Validators.required),
    Placeholder: new FormControl(''),
    Required: new FormControl(false),
    Default_Value: new FormControl(''),
    Options: new FormControl(''),
    Section_Name: new FormControl(''),
    Val_MinLength: new FormControl(null),
    Val_MaxLength: new FormControl(null),
    Val_Pattern: new FormControl(''),
    Val_Min: new FormControl(null),
    Val_Max: new FormControl(null)
  });

  public TableColumns: { name: string, type: string }[] = [];
  public ColumnTypeOptions = [
    { label: 'Texto', value: 'text_short' },
    { label: 'Numero', value: 'number' },
    { label: 'Si/No', value: 'boolean' },
    { label: 'Seleccion', value: 'select' }
  ];

  public FieldTypes = [
    { label: 'Texto corto', value: 'text_short' },
    { label: 'Texto largo', value: 'text_long' },
    { label: 'Numero', value: 'number' },
    { label: 'Fecha', value: 'date' },
    { label: 'Seleccion unica', value: 'select' },
    { label: 'Seleccion multiple', value: 'multi_select' },
    { label: 'Si/No', value: 'boolean' },
    { label: 'Imagen', value: 'image' },
    { label: 'Archivo', value: 'file' },
    { label: 'Tabla', value: 'table' },
    { label: 'Firma', value: 'signature' },
    { label: 'Seccion', value: 'section' },
    { label: 'Email', value: 'email' },
    { label: 'Teléfono', value: 'phone' },
    { label: 'URL', value: 'url' },
    { label: 'Texto Enriquecido', value: 'rich_text' },
    { label: 'Código QR', value: 'qr_code' },
    { label: 'Código de barras', value: 'barcode' },
    { label: 'Checkboxes', value: 'checkbox_list' },
    { label: 'Hora', value: 'time' }
  ];

  private ColorConfirm: string = '#20a8e1';

  constructor() {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.TemplateId = Number(idParam);
      this.IsEdit = true;
      this.LoadTemplate();
    }

    // Add welcome message
    this.ChatHistory.push({
      role: 'assistant',
      content: 'Crea describiendo lo que quieres generar o sube una foto/PDF de un documento para editarlo.',
      timestamp: new Date()
    });
  }

  private LoadTemplate(): void {
    if (!this.TemplateId) return;
    this.formTemplateService.GetFormTemplateById(this.TemplateId).subscribe({
      next: (res: Response_Forms) => {
        if (res.success) {
          const template: Form_Template_With_Fields = res.operation;
          if (template.Fields) {
            template.Fields.forEach((f: any) => f.UI_ID = f.UI_ID || Math.random().toString(36).substring(2, 9));
          }
          this.FormSchema = {
            Name: template.Name,
            Description: template.Description,
            Category: template.Category,
            Fields: template.Fields
          };
        }
      }
    });
  }

  // ======================== CHAT ACTIONS ========================

  public SendMessage(): void {
    if (!this.UserInput.trim() && !this.SelectedFile) return;

    const userMessage = this.UserInput.trim();
    this.ChatHistory.push({
      role: 'user',
      content: this.SelectedFile ? `${userMessage || 'Documento subido'} [${this.SelectedFile.name}]` : userMessage,
      timestamp: new Date()
    });

    this.IsLoading = true;
    const prompt = userMessage;
    const file = this.SelectedFile;
    this.UserInput = '';
    
    // Clear selected file and input element state
    this.RemoveFile();


    this.scrollToBottom();

    const schema = this.FormSchema.Fields.length > 0 ? this.FormSchema : null;

    this.formTemplateService.GenAIChat(prompt, schema, this.ApiChatHistory, file || undefined).subscribe({
      next: (res: Response_Forms) => {
        this.IsLoading = false;
        if (res.success && res.operation) {
          const data = res.operation;

          // Update schema
          if (data.schema) {
            if (data.schema.Fields) {
              data.schema.Fields.forEach((f: any) => f.UI_ID = f.UI_ID || Math.random().toString(36).substring(2, 9));
            }
            this.FormSchema = data.schema;
          }

          // Update chat
          if (data.message) {
            this.ChatHistory.push({
              role: 'assistant',
              content: data.message,
              timestamp: new Date()
            });
          }

          // Update API history
          if (data.chatHistory) {
            this.ApiChatHistory = data.chatHistory;
          }

          this.scrollToBottom();
        }
      },
      error: (err) => {
        this.IsLoading = false;
        this.ChatHistory.push({
          role: 'assistant',
          content: 'Hubo un error al procesar tu solicitud. Intenta de nuevo.',
          timestamp: new Date()
        });
        console.error('GenAI error:', err);
        this.scrollToBottom();
      }
    });
  }

  public OnFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.SelectedFile = input.files[0];
    }
  }

  public TriggerFileUpload(): void {
    this.fileInputEl?.nativeElement.click();
  }

  public RemoveFile(): void {
    this.SelectedFile = null;
    if (this.fileInputEl) {
      this.fileInputEl.nativeElement.value = '';
    }
  }

  public OnKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.SendMessage();
    }
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.chatMessagesEl) {
        this.chatMessagesEl.nativeElement.scrollTop = this.chatMessagesEl.nativeElement.scrollHeight;
      }
    }, 100);
  }

  // ======================== TOOLBAR ACTIONS ========================

  public InitTableColumns(): void {
    const optionsVal = this.FieldForm.get('Options')?.value;
    if (optionsVal) {
      try {
        const parsed = typeof optionsVal === 'string' ? JSON.parse(optionsVal) : optionsVal;
        if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].name) {
          this.TableColumns = parsed;
          return;
        }
      } catch (e) {}
    }
    this.TableColumns = [{ name: '', type: 'text_short' }];
  }

  public AddTableColumn(): void {
    this.TableColumns.push({ name: '', type: 'text_short' });
  }

  public RemoveTableColumn(index: number): void {
    if (this.TableColumns.length > 1) {
      this.TableColumns.splice(index, 1);
    }
  }

  public SyncTableColumnsToOptions(): void {
    const valid = this.TableColumns.filter(c => c.name.trim() !== '');
    this.FieldForm.get('Options')?.setValue(JSON.stringify(valid));
  }

  public AddField(): void {
    this.SelectedField = null;
    this.FieldForm.reset({
      Label: '',
      Field_Type: 'text_short',
      Placeholder: '',
      Required: false,
      Default_Value: '',
      Options: '',
      Section_Name: ''
    });
    this.TableColumns = [{ name: '', type: 'text_short' }];
    this.ShowFieldEditor = true;
  }

  public EditField(field: Form_Template_Field, index: number): void {
    this.SelectedField = field;

    let valMin = null, valMax = null, valMinLen = null, valMaxLen = null, valPattern = '';
    if (field.Validations) {
      try {
        const v = typeof field.Validations === 'string' ? JSON.parse(field.Validations) : field.Validations;
        valMinLen = v.minLength || null;
        valMaxLen = v.maxLength || null;
        valPattern = v.pattern || '';
        valMin = v.min !== undefined ? v.min : null;
        valMax = v.max !== undefined ? v.max : null;
      } catch (e) {}
    }

    let optionsStr = '';
    if (field.Options) {
      if (field.Field_Type === 'table') {
        optionsStr = field.Options;
      } else {
        try {
          const opts = typeof field.Options === 'string' ? JSON.parse(field.Options) : field.Options;
          optionsStr = Array.isArray(opts) ? opts.join('\n') : '';
        } catch (e) {
          optionsStr = field.Options;
        }
      }
    }

    this.FieldForm.patchValue({
      Label: field.Label,
      Field_Type: field.Field_Type,
      Placeholder: field.Placeholder || '',
      Required: field.Required,
      Default_Value: field.Default_Value || '',
      Options: optionsStr,
      Section_Name: field.Section_Name || '',
      Val_MinLength: valMinLen,
      Val_MaxLength: valMaxLen,
      Val_Pattern: valPattern,
      Val_Min: valMin,
      Val_Max: valMax
    });
    if (field.Field_Type === 'table') {
      this.InitTableColumns();
    } else {
      this.TableColumns = [{ name: '', type: 'text_short' }];
    }
    this.ShowFieldEditor = true;
  }

  public SaveField(): void {
    if (this.FieldForm.get('Label')?.invalid) {
      Swal.fire('Error', 'El campo Label es obligatorio.', 'warning');
      return;
    }

    const formVal = this.FieldForm.value;

    if (formVal.Field_Type === 'table') {
      this.SyncTableColumnsToOptions();
    }

    let options: string | null = null;
    if (formVal.Field_Type === 'table') {
      const cols = this.TableColumns.filter(c => c.name.trim() !== '');
      if (cols.length > 0) {
        options = JSON.stringify(cols);
      }
    } else if (formVal.Options && formVal.Options.trim()) {
      const optArray = formVal.Options.split('\n').map((o: string) => o.trim()).filter((o: string) => o);
      options = JSON.stringify(optArray);
    }

    let validations: string | null = null;
    const valObj: any = {};
    if (formVal.Val_MinLength) valObj.minLength = Number(formVal.Val_MinLength);
    if (formVal.Val_MaxLength) valObj.maxLength = Number(formVal.Val_MaxLength);
    if (formVal.Val_Pattern) valObj.pattern = formVal.Val_Pattern;
    if (formVal.Val_Min !== null && formVal.Val_Min !== '') valObj.min = Number(formVal.Val_Min);
    if (formVal.Val_Max !== null && formVal.Val_Max !== '') valObj.max = Number(formVal.Val_Max);
    if (Object.keys(valObj).length > 0) validations = JSON.stringify(valObj);

    const field: Form_Template_Field = {
      UI_ID: this.SelectedField?.UI_ID || Math.random().toString(36).substring(2, 9),
      Label: formVal.Label,
      Field_Type: formVal.Field_Type,
      Placeholder: formVal.Placeholder || null,
      Required: formVal.Required || false,
      Default_Value: formVal.Default_Value || null,
      Options: options,
      Validations: validations,
      Field_Order: 0,
      Section_Name: formVal.Section_Name || null
    };

    if (this.SelectedField) {
      const index = this.FormSchema.Fields.indexOf(this.SelectedField);
      if (index >= 0) {
        field.Field_Order = this.SelectedField.Field_Order;
        field.ID = this.SelectedField.ID;
        this.FormSchema.Fields[index] = field;
        this.FormSchema.Fields = [...this.FormSchema.Fields];
      }
    } else {
      field.Field_Order = this.FormSchema.Fields.length + 1;
      this.FormSchema.Fields.push(field);
      this.FormSchema.Fields = [...this.FormSchema.Fields];
    }

    this.ShowFieldEditor = false;
    this.SelectedField = null;
  }

  public CancelFieldEditor(): void {
    this.ShowFieldEditor = false;
    this.SelectedField = null;
  }

  public RemoveField(index: number): void {
    this.FormSchema.Fields.splice(index, 1);
    this.ReorderFields();
    this.FormSchema.Fields = [...this.FormSchema.Fields];
  }

  public DropField(event: CdkDragDrop<Form_Template_Field[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.FormSchema.Fields, event.previousIndex, event.currentIndex);
    this.ReorderFields();
    this.FormSchema.Fields = [...this.FormSchema.Fields];
  }

  private ReorderFields(): void {
    this.FormSchema.Fields.forEach((f, i) => f.Field_Order = i + 1);
  }


  public GetFieldIcon(type: string): string {
    const icons: { [key: string]: string } = {
      'text_short': 'fa-solid fa-font',
      'text_long': 'fa-solid fa-align-left',
      'rich_text': 'fa-solid fa-pen-nib',
      'number': 'fa-solid fa-hashtag',
      'date': 'fa-solid fa-calendar',
      'time': 'fa-solid fa-clock',
      'email': 'fa-solid fa-envelope',
      'phone': 'fa-solid fa-phone',
      'url': 'fa-solid fa-link',
      'select': 'fa-solid fa-caret-down',
      'multi_select': 'fa-solid fa-list-check',
      'checkbox_list': 'fa-solid fa-square-check',
      'boolean': 'fa-solid fa-toggle-on',
      'image': 'fa-solid fa-image',
      'file': 'fa-solid fa-paperclip',
      'qr_code': 'fa-solid fa-qrcode',
      'barcode': 'fa-solid fa-barcode',
      'table': 'fa-solid fa-table-cells',
      'section': 'fa-solid fa-grip-lines',
      'signature': 'fa-solid fa-signature'
    };
    return icons[type] || 'fa-solid fa-question';
  }

  public GetFieldTypeLabel(type: string): string {
    const found = this.FieldTypes.find(ft => ft.value === type);
    return found ? found.label : type;
  }

  // ======================== SAVE ========================

  public SaveAsDraft(): void {
    if (!this.FormSchema.Name.trim()) {
      Swal.fire('Error', 'El formulario no tiene nombre. Pidele al asistente que le ponga un nombre.', 'warning');
      return;
    }
    if (this.FormSchema.Fields.length === 0) {
      Swal.fire('Error', 'El formulario no tiene campos.', 'warning');
      return;
    }

    const data = {
      Name: this.FormSchema.Name,
      Description: this.FormSchema.Description,
      Category: this.FormSchema.Category,
      Status: 1,
      GenAI_Source: true,
      Fields: this.FormSchema.Fields.map((f, i) => ({
        ...f,
        Field_Order: f.Field_Order || i + 1
      }))
    };

    const request$ = this.IsEdit && this.TemplateId
      ? this.formTemplateService.UpdateFormTemplate(this.TemplateId, data)
      : this.formTemplateService.CreateFormTemplate(data);

    request$.subscribe({
      next: (res: Response_Forms) => {
        if (res.success) {
          const finalId = res.operation?.ID || this.TemplateId;
          
          this.formTemplateService.ChangeStatusFormTemplate(finalId, 1).subscribe({
            next: () => {
              Swal.fire('Guardado', 'Formulario guardado como borrador.', 'success');
              this.router.navigate(['/Dashboard/General/FormTemplates']);
            },
            error: () => Swal.fire('Error', 'Se guardó, pero hubo un error al cambiar su estado.', 'error')
          });
        }
      },
      error: () => {
        Swal.fire('Error', 'No se pudo guardar el formulario.', 'error');
      }
    });
  }

  public PublishTemplate(): void {
    if (!this.FormSchema.Name.trim() || this.FormSchema.Fields.length === 0) {
      Swal.fire('Error', 'El formulario necesita un nombre y al menos un campo.', 'warning');
      return;
    }

    const data = {
      Name: this.FormSchema.Name,
      Description: this.FormSchema.Description,
      Category: this.FormSchema.Category,
      Status: 2,
      GenAI_Source: true,
      Fields: this.FormSchema.Fields.map((f, i) => ({
        ...f,
        Field_Order: f.Field_Order || i + 1
      }))
    };

    const request$ = this.IsEdit && this.TemplateId
      ? this.formTemplateService.UpdateFormTemplate(this.TemplateId, data)
      : this.formTemplateService.CreateFormTemplate(data);

    request$.subscribe({
      next: (res: Response_Forms) => {
        if (res.success) {
          const finalId = this.TemplateId || res.operation.ID;
          
          // Explicitly change status to active since API saves as draft
          this.formTemplateService.ChangeStatusFormTemplate(finalId, 2).subscribe({
            next: () => {
              Swal.fire('Publicado', 'Formulario publicado correctamente.', 'success');
              this.router.navigate(['/Dashboard/General/FormTemplates']);
            },
            error: () => Swal.fire('Error', 'Se guardó, pero hubo un error al publicar.', 'error')
          });
        }
      },
      error: () => {
        Swal.fire('Error', 'No se pudo publicar el formulario.', 'error');
      }
    });
  }

  public GoBack(): void {
    this.router.navigate(['/Dashboard/General/FormTemplates']);
  }
}
